/**
 * Номера и названия указателей режима «Навигация» (docs/wayfinding/plan.md §9.1.2).
 *
 * Название — ПРАВИЛО ЧТЕНИЯ: шаблон карты, в который подставлены код типа, индекс
 * проекта, номер плана и номер указателя. В свойства фичи оно не пишется: иначе правка
 * шаблона, кода типа, индекса или порядка планов обязана была бы переписывать все
 * указатели карты, а любой пропущенный путь оставил бы старые названия. Хранится только
 * номер — единственное, что нельзя вывести.
 *
 * Своё название, заданное руками (`WAYFINDING_SIGN_OWN_NAME_PROPERTY`), стоит поверх шаблона:
 * человек переименовал указатель — значит, так его и зовут везде.
 *
 * Считают название редактор (панель слоёв, правая панель), вьювер и выгрузка — все через
 * `wayfindingSignName`, иначе ведомость и экран разошлись бы.
 */
import {
  DEFAULT_WAYFINDING_SIGN_NAME_TEMPLATE,
  DEFAULT_WAYFINDING_SIGN_NAME_TEMPLATE_WITHOUT_PLANS,
  FRAME_PROPERTY_KEY,
  WAYFINDING_SIGN_NAME_LABEL_PROPERTY,
  WAYFINDING_SIGN_NUMBER_PROPERTY,
  WAYFINDING_SIGN_OWN_NAME_PROPERTY,
  wayfindingLayerKind,
  type WayfindingNumberingScope,
} from '../types/wayfinding.js';
import type { CustomPropertyDef } from '../types/layer.js';
import {
  DEFAULT_FEATURE_NAME_CUSTOM_PROPERTY,
  DEFAULT_FEATURE_NAME_PROPERTY,
  isNamedTitleProperty,
} from './ensureNameCustomProperty.js';
import { readWayfindingSignTypes, resolveWayfindingSignTypeId } from './wayfindingSignTypes.js';
import { renderTemplateTokens, renderedTemplateLabel, type RenderedTemplate } from './templateTokens.js';

/**
 * Номер указателя как поле схемы — ради таблиц (✏️ 13.09): таблица слоя и таблица по группе
 * показывают колонки схемы, а номер пишется в свойства мимо неё и в таблицах не был виден.
 * Поле числовое — правка ячейки кладёт число, как и автонумерация.
 */
export function wayfindingSignNumberFieldDef(): CustomPropertyDef {
  return { name: WAYFINDING_SIGN_NUMBER_PROPERTY, label: 'Номер', type: 'number' };
}

function hasDefNamed(def: unknown, name: string): boolean {
  return !!def && typeof def === 'object' && (def as { name?: unknown }).name === name;
}

/**
 * Схема слоя указателей для ТАБЛИЦЫ: с полем названия (в нём таблица показывает название
 * по шаблону) и полем номера сразу за ним; `null` — оба на месте (любого типа — их завёл
 * человек) и менять нечего. Правило чтения: в слой пишется, только если человек сам правит
 * схему из таблицы (перестановка колонок) — тогда поля честно остаются.
 */
export function withWayfindingSignNumberField(customProperties: unknown): CustomPropertyDef[] | null {
  const defs: unknown[] = Array.isArray(customProperties) ? customProperties : [];
  const hasNumber = defs.some((def) => hasDefNamed(def, WAYFINDING_SIGN_NUMBER_PROPERTY));
  // Поле названия — то же, что найдёт `resolveFeatureNameKey`.
  const nameIndex = defs.findIndex(
    (def) => isNamedTitleProperty(def) || hasDefNamed(def, DEFAULT_FEATURE_NAME_PROPERTY),
  );
  if (hasNumber && nameIndex >= 0) {
    return null;
  }
  const numberDefs = hasNumber ? [] : [wayfindingSignNumberFieldDef()];
  if (nameIndex < 0) {
    return [{ ...DEFAULT_FEATURE_NAME_CUSTOM_PROPERTY }, ...numberDefs, ...(defs as CustomPropertyDef[])];
  }
  const result = [...(defs as CustomPropertyDef[])];
  result.splice(nameIndex + 1, 0, ...numberDefs);
  return result;
}

/** Переменные шаблона названия — в порядке, в каком их предлагает интерфейс. */
export const WAYFINDING_SIGN_NAME_VARIABLES = [
  { key: 'typeCode', label: 'Код типа' },
  { key: 'projectIndex', label: 'Индекс проекта' },
  { key: 'planNumber', label: 'Номер плана' },
  { key: 'signNumber', label: 'Номер указателя' },
] as const;

export type WayfindingSignNameVariable = (typeof WAYFINDING_SIGN_NAME_VARIABLES)[number]['key'];

export type WayfindingSignNameValues = Partial<
  Record<WayfindingSignNameVariable, string | number | null | undefined>
>;

const KNOWN_VARIABLES: ReadonlySet<string> = new Set(
  WAYFINDING_SIGN_NAME_VARIABLES.map((variable) => variable.key),
);

/**
 * `{имя}` или `{имя:N}` — N дополняет значение нулями слева до N знаков («{signNumber:3}»
 * → «007»). Ширина ограничена двумя цифрами: больше в названии носителя не бывает.
 */
const TOKEN = /\{([A-Za-z]+)(?::(\d{1,2}))?\}/g;

/** Номера больше не бывают; заодно отсекает `1e308` и прочий мусор из свободного jsonb. */
export const WAYFINDING_SIGN_NUMBER_MAX = 1_000_000_000;

function renderTemplate(template: string, values: WayfindingSignNameValues): RenderedTemplate {
  return renderTemplateTokens(template, TOKEN, ([name = '', width]) => {
    // Незнакомое имя остаётся как написано: опечатка видна в названии, а не пропадает молча.
    if (!KNOWN_VARIABLES.has(name)) {
      return null;
    }
    const raw = values[name as WayfindingSignNameVariable];
    const value = raw === null || raw === undefined ? '' : String(raw);
    if (value === '') {
      return '';
    }
    // Индекс плана нулями не дополняется: «L2» под `{planNumber:3}` остаётся «L2». Только у
    // `planNumber` — у прочих переменных ширина работает как прежде, и названия не меняются.
    const keepAsIs = name === 'planNumber' && !/^\d+$/.test(value);
    return width && !keepAsIs ? value.padStart(Number(width), '0') : value;
  });
}

/**
 * Подстановка значений в шаблон. Всё, что не переменная, остаётся как написано: какие
 * переменные брать и чем их отбивать, решает человек. Пустая переменная даёт пустоту —
 * соседние разделители не схлопываются, иначе название врало бы о своей структуре.
 */
export function formatWayfindingSignName(template: string, values: WayfindingSignNameValues): string {
  return renderTemplate(template, values).text;
}

function readObject(value: unknown): Record<string, unknown> | null {
  return value && typeof value === 'object' && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : null;
}

export interface WayfindingNamingSettings {
  /** Индекс проекта; '' — не задан. */
  projectIndex: string;
  numberingScope: WayfindingNumberingScope;
  /** Счёт начинается заново на каждом плане (внутри области нумерации). */
  numberingPerPlan: boolean;
  /** Действующий шаблон: заданный на карте, иначе шаблон по умолчанию. */
  signNameTemplate: string;
}

/** Нумерация и шаблон названий из свободного jsonb настроек карты. */
export function readWayfindingNaming(settings: unknown): WayfindingNamingSettings {
  const navigation = readObject(readObject(settings)?.navigation) ?? {};
  const template = navigation.signNameTemplate;
  const hasPlans = wayfindingMapHasPlans(settings);
  return {
    projectIndex: typeof navigation.projectIndex === 'string' ? navigation.projectIndex.trim() : '',
    numberingScope: navigation.numberingScope === 'type' ? 'type' : 'all',
    // Без планов делить счёт не по чему: сохранённый выбор вернётся вместе с планами.
    numberingPerPlan: hasPlans && navigation.numberingPerPlan === true,
    signNameTemplate:
      typeof template === 'string' && template.trim() !== ''
        ? template
        : defaultWayfindingSignNameTemplate(settings),
  };
}

/**
 * Навигация по планам — только на карте-изображении: на обычной карте указатели стоят прямо
 * по базовой карте, планов нет (plan.md §5.7). Тип карты — `settings.mapType`, по умолчанию
 * обычная карта.
 */
export function wayfindingMapHasPlans(settings: unknown): boolean {
  return readObject(settings)?.mapType === 'image';
}

/**
 * Шаблон названия по умолчанию — по типу карты (✏️ 19.09): на обычной карте без номера
 * плана. Правило чтения: сменили тип карты — у карты на умолчании сменились и названия.
 */
export function defaultWayfindingSignNameTemplate(settings: unknown): string {
  return wayfindingMapHasPlans(settings)
    ? DEFAULT_WAYFINDING_SIGN_NAME_TEMPLATE
    : DEFAULT_WAYFINDING_SIGN_NAME_TEMPLATE_WITHOUT_PLANS;
}

/**
 * Номер указателя; `null` — не проставлен. Строку из цифр принимаем: так номер лежал до
 * автонумерации, и «Пересчитать» должен сохранить порядок этих указателей, а не свалить
 * их в конец. Та же проверка — в SQL пересчёта (`wayfinding-sign-numbers.service.ts`).
 */
export function readWayfindingSignNumber(
  properties: Record<string, unknown> | null | undefined,
): number | null {
  const value = properties?.[WAYFINDING_SIGN_NUMBER_PROPERTY];
  if (typeof value === 'number') {
    return Number.isFinite(value) && value >= 1 && value <= WAYFINDING_SIGN_NUMBER_MAX
      ? Math.floor(value)
      : null;
  }
  if (typeof value === 'string' && /^\s*\d{1,9}\s*$/.test(value)) {
    const parsed = Number(value.trim());
    return parsed >= 1 ? parsed : null;
  }
  return null;
}

/**
 * Указатель — точка в слое указателей. Тот же признак, что у панели указателя: у узлов
 * графа и точек общих слоёв ни номера, ни названия по шаблону нет.
 */
export function isWayfindingSignFeature(
  sourceConfig: unknown,
  geometryType: string | null | undefined,
): boolean {
  return (
    geometryType === 'Point' &&
    wayfindingLayerKind(sourceConfig as Record<string, unknown> | null | undefined) === 'signs'
  );
}

/** Всё, что нужно названию указателя, кроме самого указателя. Собирается раз на рендер. */
export interface WayfindingSignNameContext {
  template: string;
  projectIndex: string;
  typeCodeById: ReadonlyMap<string, string>;
  /**
   * Значение `{planNumber}`: индекс плана, если он задан (`readWayfindingPlanIndexes`), иначе
   * место в колонке «Планы» с единицы.
   */
  planNumberByFrameId: ReadonlyMap<string, number | string>;
}

/**
 * Ключ индексов планов в `settings.navigation` (✏️ 04.10): id плана → индекс («L2», «-1»).
 *
 * В настройках карты, а не колонкой плана (решение Паши): индекс — часть нумерации, как
 * индекс проекта. Копия плана получает новый id и индекса не наследует — у копии он свой,
 * а пока не задан, в названиях стоит номер по порядку. Запись удалённого плана ничему не
 * мешает: план не найдётся, и индекс просто не прочитается.
 */
export const WAYFINDING_PLAN_INDEXES_KEY = 'planIndexByFrameId';

/** Индексы планов из настроек карты; пустые и не строки отброшены. */
export function readWayfindingPlanIndexes(settings: unknown): ReadonlyMap<string, string> {
  const raw = readObject(readObject(readObject(settings)?.navigation)?.[WAYFINDING_PLAN_INDEXES_KEY]);
  const out = new Map<string, string>();
  for (const [frameId, value] of Object.entries(raw ?? {})) {
    if (typeof value === 'string' && value.trim() !== '') {
      out.set(frameId, value.trim());
    }
  }
  return out;
}

/**
 * Номер плана — место в колонке «Планы» с единицы, по порядку слотов, а не `slot + 1`: слоты
 * уникальны, но не обязаны идти подряд, а человек видит в колонке ровно порядок.
 */
export function wayfindingPlanOrdinals(
  frames: ReadonlyArray<{ id: string; slot: number }> | null | undefined,
): Map<string, number> {
  const ordered = [...(frames ?? [])].sort((a, b) => a.slot - b.slot);
  return new Map(ordered.map((frame, index) => [frame.id, index + 1]));
}

export function createWayfindingSignNameContext(params: {
  settings: unknown;
  frames?: ReadonlyArray<{ id: string; slot: number }> | null;
}): WayfindingSignNameContext {
  const naming = readWayfindingNaming(params.settings);
  const typeCodeById = new Map(
    readWayfindingSignTypes(params.settings).map((type) => [type.id, type.code ?? '']),
  );
  // Заданный индекс плана стоит вместо номера по порядку.
  const planIndexes = readWayfindingPlanIndexes(params.settings);
  const planNumberByFrameId = new Map<string, number | string>(
    [...wayfindingPlanOrdinals(params.frames)].map(([frameId, ordinal]) => [
      frameId,
      planIndexes.get(frameId) ?? ordinal,
    ]),
  );
  return {
    template: naming.signNameTemplate,
    projectIndex: naming.projectIndex,
    typeCodeById,
    planNumberByFrameId,
  };
}

/** `layerSignTypeId` — тип носителя слоя: код типа указателя без своего типа — от слоя. */
export function wayfindingSignNameValues(
  properties: Record<string, unknown> | null | undefined,
  context: WayfindingSignNameContext,
  layerSignTypeId: string | null = null,
): WayfindingSignNameValues {
  const typeId = resolveWayfindingSignTypeId(properties, layerSignTypeId, context.typeCodeById);
  const frameId = properties?.[FRAME_PROPERTY_KEY];
  return {
    typeCode: typeId ? (context.typeCodeById.get(typeId) ?? '') : '',
    projectIndex: context.projectIndex,
    planNumber: typeof frameId === 'string' ? (context.planNumberByFrameId.get(frameId) ?? null) : null,
    signNumber: readWayfindingSignNumber(properties),
  };
}

/** Своё название указателя, заданное руками; `''` — не задано. */
export function readWayfindingSignOwnName(
  properties: Record<string, unknown> | null | undefined,
): string {
  const value = properties?.[WAYFINDING_SIGN_OWN_NAME_PROPERTY];
  return typeof value === 'string' ? value.trim() : '';
}

/**
 * Название указателя: своё (`readWayfindingSignOwnName`), иначе по шаблону карты.
 *
 * `''` — шаблон не дал ничего своего: все его переменные пусты (тип без кода, указатель
 * вне планов, без номера, индекс не задан). Тогда вызывающий показывает штатное имя
 * объекта — строка из одних разделителей «___» ничего бы не называла. Шаблон без
 * переменных вовсе — осознанный выбор человека, его текст возвращается как есть.
 */
export function wayfindingSignName(
  properties: Record<string, unknown> | null | undefined,
  context: WayfindingSignNameContext,
  layerSignTypeId: string | null = null,
): string {
  const own = readWayfindingSignOwnName(properties);
  return own !== '' ? own : wayfindingSignTemplateName(properties, context, layerSignTypeId);
}

/**
 * Составной номер указателя — название ПО ШАБЛОНУ, без своего названия поверх (✏️ 13.09: поле
 * «Полный номер» рядом с номером в панели указателя). `''` — все переменные шаблона пусты.
 */
export function wayfindingSignTemplateName(
  properties: Record<string, unknown> | null | undefined,
  context: WayfindingSignNameContext,
  layerSignTypeId: string | null = null,
): string {
  return renderedTemplateLabel(
    renderTemplate(context.template, wayfindingSignNameValues(properties, context, layerSignTypeId)),
  );
}

interface SignFeatureLike {
  geometry?: { type?: string } | null;
  properties?: Record<string, unknown> | null;
}

/**
 * Данные слоя указателей ДЛЯ КАРТЫ: у каждой точки — вычисленное название в
 * `WAYFINDING_SIGN_NAME_LABEL_PROPERTY`, его читает подпись слоя по умолчанию
 * (`withWayfindingSignLabelDefaults`). Копия, а не правка: в базу название не пишется.
 *
 * Ничего не поменялось — возвращается та же коллекция: синк карты сравнивает данные по
 * ссылке, и новая копия на каждый рендер гоняла бы `setData` впустую.
 */
export function withWayfindingSignNameLabels<C extends { features: readonly SignFeatureLike[] }>(
  collection: C,
  context: WayfindingSignNameContext,
  layerSignTypeId: string | null = null,
): C {
  let changed = false;
  const features = collection.features.map((feature) => {
    if (feature.geometry?.type !== 'Point') {
      return feature;
    }
    const name = wayfindingSignName(feature.properties, context, layerSignTypeId);
    if ((feature.properties?.[WAYFINDING_SIGN_NAME_LABEL_PROPERTY] ?? '') === name) {
      return feature;
    }
    changed = true;
    return {
      ...feature,
      properties: { ...feature.properties, [WAYFINDING_SIGN_NAME_LABEL_PROPERTY]: name },
    };
  });
  return changed ? { ...collection, features } : collection;
}

/**
 * Отпечаток контекста названий. Настройки карты приходят с сервера новыми объектами на
 * каждом перечитывании проекта, и контекст по ссылке менялся бы без повода — а за ним
 * данные карты и `setData` у всех слоёв указателей.
 */
export function wayfindingSignNameContextKey(context: WayfindingSignNameContext): string {
  return JSON.stringify([
    context.template,
    context.projectIndex,
    [...context.typeCodeById],
    [...context.planNumberByFrameId],
  ]);
}

/** Обратно из отпечатка — контекст, равный по содержимому исходному. */
export function wayfindingSignNameContextFromKey(key: string): WayfindingSignNameContext {
  const [template, projectIndex, typeCodes, planNumbers] = JSON.parse(key) as [
    string,
    string,
    Array<[string, string]>,
    Array<[string, number | string]>,
  ];
  return {
    template,
    projectIndex,
    typeCodeById: new Map(typeCodes),
    planNumberByFrameId: new Map(planNumbers),
  };
}

/**
 * Область нумерации указателя: у сквозной она одна на карту, у нумерации по типам — тип.
 * При «на каждом плане сначала» область дополнительно делится по плану: указатели вне планов
 * считаются своей общей областью.
 *
 * Разделитель — ` `: в id типа и плана его не бывает, и «тип a + план b» не совпадёт
 * с другой парой.
 */
export function wayfindingNumberingBucket(
  scope: WayfindingNumberingScope,
  typeId: string | null,
  perPlan = false,
  frameId: string | null = null,
): string {
  const typePart = scope === 'type' ? (typeId ?? '') : '';
  return perPlan ? `${typePart} ${frameId ?? ''}` : typePart;
}

export interface WayfindingSignNumberEntry {
  typeId: string | null;
  number: number | null;
  /** План указателя; `null` или нет — вне планов. Нужен только при нумерации по планам. */
  frameId?: string | null;
}

/**
 * Номер нового указателя: наибольший в его области плюс один. Пропуски после удаления
 * не заполняются — это дело «Пересчитать»: номер, выданный в дыру, ломал бы порядок
 * «по мере создания».
 *
 * @param perPlan счёт на каждом плане свой; `frameId` — план нового указателя.
 */
export function nextWayfindingSignNumber(
  signs: Iterable<WayfindingSignNumberEntry>,
  scope: WayfindingNumberingScope,
  typeId: string | null,
  perPlan = false,
  frameId: string | null = null,
): number {
  const bucket = wayfindingNumberingBucket(scope, typeId, perPlan, frameId);
  let max = 0;
  for (const sign of signs) {
    if (
      sign.number !== null &&
      sign.number > max &&
      wayfindingNumberingBucket(scope, sign.typeId, perPlan, sign.frameId ?? null) === bucket
    ) {
      max = sign.number;
    }
  }
  return max + 1;
}

/**
 * Нумерация «чистая»: в каждой области номера ровно 1…N — без пропусков, повторов
 * и указателей без номера. Иначе интерфейс предлагает «Пересчитать».
 */
export function isWayfindingNumberingCompact(
  signs: Iterable<WayfindingSignNumberEntry>,
  scope: WayfindingNumberingScope,
  perPlan = false,
): boolean {
  const buckets = new Map<string, number[]>();
  for (const sign of signs) {
    if (sign.number === null) {
      return false;
    }
    const bucket = wayfindingNumberingBucket(scope, sign.typeId, perPlan, sign.frameId ?? null);
    const list = buckets.get(bucket) ?? [];
    list.push(sign.number);
    buckets.set(bucket, list);
  }
  for (const numbers of buckets.values()) {
    numbers.sort((a, b) => a - b);
    if (numbers.some((value, index) => value !== index + 1)) {
      return false;
    }
  }
  return true;
}
